import type { DietaryConcernReason, ExplainDishResponse, ParseResponse, SavedOrder } from '../../shared/menu';

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let code = 'INTERNAL';
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error?.code) code = body.error.code;
      if (body?.error?.message) message = body.error.message;
    } catch { /* ignore */ }
    throw new ApiError(code, message, res.status);
  }
  return (await res.json()) as T;
}

export interface ParseImagesArgs {
  files: File[];
  targetLanguage: string;
  signal?: AbortSignal;
  // When set, server prepends the previous menu's stored page images and
  // re-parses everything together. Used by "Add page" from MenuScreen.
  previousMenuId?: string;
}

export async function parseMenu({ files, targetLanguage, signal, previousMenuId }: ParseImagesArgs): Promise<ParseResponse> {
  const form = new FormData();
  form.set('targetLanguage', targetLanguage);
  if (previousMenuId) form.set('previousMenuId', previousMenuId);
  for (const f of files) form.append('images', f);

  const res = await fetch('/api/menu/parse', { method: 'POST', body: form, signal });
  return handle<ParseResponse>(res);
}

export async function fetchMenu(menuId: string, signal?: AbortSignal): Promise<ParseResponse> {
  const res = await fetch(`/api/menu/${menuId}`, { signal });
  return handle<ParseResponse>(res);
}

export interface ExplainDishArgs {
  menuId: string;
  dishId: string;
  targetLanguage: string;
  dietaryReason?: DietaryConcernReason | null;
  signal?: AbortSignal;
}

export async function explainDish({
  menuId,
  dishId,
  targetLanguage,
  dietaryReason,
  signal,
}: ExplainDishArgs): Promise<ExplainDishResponse> {
  const res = await fetch(`/api/menu/${menuId}/dish/${dishId}/explain`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      targetLanguage,
      ...(dietaryReason ? { dietaryReason } : {}),
    }),
    signal,
  });
  return handle<ExplainDishResponse>(res);
}

export interface MeResponse {
  user: { id: string; sessionId: string; email: string; isAdmin: boolean } | null;
}

export async function fetchMe(): Promise<MeResponse> {
  const res = await fetch('/api/me');
  return handle<MeResponse>(res);
}

export interface StartEmailCodeResponse {
  ok: true;
  delivered: boolean;
  expiresInSec: number;
  devCode: string | null;
}

export async function startEmailCode(email: string): Promise<StartEmailCodeResponse> {
  const res = await fetch('/api/auth/email/start', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return handle<StartEmailCodeResponse>(res);
}

export async function verifyEmailCode(email: string, code: string): Promise<{ ok: true; userId: string }> {
  const res = await fetch('/api/auth/email/verify', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  return handle<{ ok: true; userId: string }>(res);
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' });
}

export interface SaveStatus {
  saved: boolean;
  signedIn: boolean;
}

export async function fetchSaveStatus(menuId: string): Promise<SaveStatus> {
  const res = await fetch(`/api/menu/${menuId}/save`);
  return handle<SaveStatus>(res);
}

export async function saveMenu(menuId: string): Promise<void> {
  const res = await fetch(`/api/menu/${menuId}/save`, { method: 'POST' });
  await handle(res);
}

export async function unsaveMenu(menuId: string): Promise<void> {
  const res = await fetch(`/api/menu/${menuId}/save`, { method: 'DELETE' });
  await handle(res);
}

export interface SavedMenuListItem {
  menuId: string;
  restaurantName: string | null;
  sourceLanguage: string;
  targetLanguage: string;
  savedAt: string;
}

export async function fetchSavedMenus(): Promise<{ saved: SavedMenuListItem[] }> {
  const res = await fetch('/api/me/saved');
  return handle<{ saved: SavedMenuListItem[] }>(res);
}

export async function syncOrder(order: SavedOrder): Promise<{ ok: true; id: string; syncedAt: string }> {
  const res = await fetch('/api/orders', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(order),
  });
  return handle<{ ok: true; id: string; syncedAt: string }>(res);
}

export async function fetchOrders(): Promise<{ orders: SavedOrder[] }> {
  const res = await fetch('/api/me/orders');
  return handle<{ orders: SavedOrder[] }>(res);
}

export async function deleteOrderRemote(id: string): Promise<void> {
  const res = await fetch(`/api/orders/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await handle(res);
}
